import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { JsonView } from "@/components/json-view";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { StatCard } from "@/components/stat-card";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { apiGet } from "@/lib/api-client";
import { asCurrency, formatRelativeTime } from "@/lib/format";
import { asRecord, asArray } from "@/lib/type-utils";

export function TraderIntelligencePage() {
  const [threshold, setThreshold] = useState(10000);

  const whaleFlowQuery = useQuery({
    queryKey: ["trader-intelligence", "whale-flow", threshold],
    queryFn: () => apiGet<unknown>(`/api/signals/whale-flow?min_value=${threshold}&limit=50`),
    refetchInterval: 15_000,
  });

  const data = asRecord(whaleFlowQuery.data);
  // Backend shape (WhaleFlowListResponse): whale_trades[] rows are grouped by
  // market+side — {market_id, side, total_value, trade_count, avg_price,
  // min_price, max_price, latest_trade}. There is no per-wallet field.
  const trades = asArray(data.whale_trades);
  const summary = asRecord(data.summary);
  const hasData = whaleFlowQuery.isSuccess;

  const totalTradeCount = trades.reduce((sum, t) => sum + (Number(t.trade_count) || 0), 0);
  const totalVolume = trades.reduce((sum, t) => sum + (Number(t.total_value) || 0), 0);
  const uniqueMarkets = new Set(trades.map((t) => String(t.market_id))).size;
  const buyVolume = Number(summary.total_buy_volume) || 0;
  const sellVolume = Number(summary.total_sell_volume) || 0;
  const netFlow = buyVolume - sellVolume;
  const buySellRatio = Number(summary.buy_sell_ratio) || 0;

  const topFlows = [...trades]
    .sort((a, b) => (Number(b.total_value) || 0) - (Number(a.total_value) || 0))
    .slice(0, 10);

  return (
    <div className="space-y-4 md:space-y-6">
      {whaleFlowQuery.isError ? (
        <QueryErrorState what="whale-flow data" onRetry={() => void whaleFlowQuery.refetch()} />
      ) : null}

      <Panel
        title="Trader Intelligence"
        description="Track large trades and whale flow grouped by market and side."
        actions={
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <Select className="text-sm" value={String(threshold)} onChange={(e) => setThreshold(Number(e.target.value))}>
              <option value="10000">$10K+ trades</option>
              <option value="50000">$50K+ trades</option>
              <option value="100000">$100K+ trades</option>
              <option value="500000">$500K+ trades</option>
            </Select>
            <Button
              variant="ghost"
              className="text-sm"
              onClick={() => whaleFlowQuery.refetch()}
              disabled={whaleFlowQuery.isFetching}
            >
              {whaleFlowQuery.isFetching ? "Refreshing..." : "↻ Refresh"}
            </Button>
          </div>
        }
      >
        <div className="grid gap-2 md:gap-3 grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
          <StatCard
            label="Whale Trades"
            value={hasData ? String(totalTradeCount) : "—"}
            hint="Trades above threshold (last 5 min)"
          />
          <StatCard
            label="Total Volume"
            value={hasData ? asCurrency(totalVolume) : "—"}
            hint="Combined volume of whale trades"
          />
          <StatCard
            label="Markets With Flow"
            value={hasData ? String(uniqueMarkets) : "—"}
            hint="Distinct markets with whale activity"
          />
          <StatCard
            label="Net Buy Flow"
            value={hasData ? asCurrency(Math.abs(netFlow)) : "—"}
            tone={hasData && netFlow > 0 ? "success" : hasData && netFlow < 0 ? "danger" : "default"}
            hint={!hasData ? "Unavailable" : netFlow > 0 ? "Bullish flow" : netFlow < 0 ? "Bearish flow" : "Neutral"}
          />
          <StatCard
            label="Buy/Sell Ratio"
            value={hasData ? buySellRatio.toFixed(2) : "—"}
            tone={hasData && buySellRatio > 1.5 ? "success" : hasData && buySellRatio < 0.67 && buySellRatio > 0 ? "danger" : "default"}
            hint={!hasData ? "Unavailable" : buySellRatio > 1 ? "More buying" : "More selling"}
          />
        </div>
      </Panel>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel title="🏆 Top Market Flows" description="Highest whale volume by market and side">
          <div className="space-y-3">
            {topFlows.length === 0 ? (
              <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
                {whaleFlowQuery.isError
                  ? "Whale-flow data unavailable"
                  : "No whale trades detected with current threshold"}
              </div>
            ) : (
              topFlows.map((flow, index) => {
                const marketId = String(flow.market_id ?? "");
                const side = String(flow.side ?? "").toUpperCase();
                return (
                  <div
                    key={`${marketId}-${side}`}
                    className="flex items-center justify-between rounded-lg bg-gunmetal/50 p-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gold-500/20 text-gold-400 text-sm font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <div className="mono text-sm font-medium">
                          {marketId.length > 16 ? `${marketId.slice(0, 10)}...${marketId.slice(-6)}` : marketId || "—"}
                        </div>
                        <div className="text-xs text-slate-400">
                          {Number(flow.trade_count) || 0} trades ·{" "}
                          <span className={side === "BUY" ? "text-green-400" : side === "SELL" ? "text-red-400" : ""}>
                            {side || "—"}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-gold-400">{asCurrency(Number(flow.total_value) || 0)}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Panel>

        <Panel
          title="📊 Recent Whale Activity"
          description="Live whale flow above threshold"
          actions={
            <span className="flex items-center gap-2 text-sm text-gold-400">
              <span className="h-2 w-2 animate-pulse rounded-full bg-gold-400" />
              Live
            </span>
          }
        >
          <div className="max-h-[400px] space-y-2 overflow-y-auto">
            {trades.length === 0 ? (
              <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
                {whaleFlowQuery.isError ? "Whale-flow data unavailable" : "No recent whale activity"}
              </div>
            ) : (
              trades.slice(0, 20).map((trade, index) => {
                const side = String(trade.side ?? "").toUpperCase();
                const tradeSentiment = side === "BUY" ? "📈" : "📉";
                const latest = trade.latest_trade;
                return (
                  <div
                    key={index}
                    className="flex items-center justify-between rounded-lg bg-gunmetal/50 p-3"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">
                        {tradeSentiment}
                      </span>
                      <div>
                        <div className="mono text-sm font-medium">{String(trade.market_id || "Unknown market")}</div>
                        <div className="text-xs text-slate-400">
                          {Number(trade.trade_count) || 0} trades · avg {(Number(trade.avg_price) || 0).toFixed(3)}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold">{asCurrency(Number(trade.total_value) || 0)}</div>
                      <div className="text-xs text-slate-400">{latest ? formatRelativeTime(latest) : "—"}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Panel>
      </div>

      <Panel title="💡 Whale Intelligence" description="Smart money signals and insights">
        <div className="grid grid-cols-1 gap-2 md:gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg bg-gunmetal/50 p-4">
            <div className="mb-2 text-sm font-semibold text-green-400">🎯 Smart Money Signal</div>
            <p className="text-xs text-slate-400">
              When 3+ whales buy the same market within 1 hour, it is often a leading indicator of price
              movement.
            </p>
          </div>
          <div className="rounded-lg bg-gunmetal/50 p-4">
            <div className="mb-2 text-sm font-semibold text-yellow-400">⚠️ Whale Divergence</div>
            <p className="text-xs text-slate-400">
              When whale sentiment differs from market price, watch for potential reversals or confirmation.
            </p>
          </div>
          <div className="rounded-lg bg-gunmetal/50 p-4">
            <div className="mb-2 text-sm font-semibold text-gold-400">📈 Volume Clustering</div>
            <p className="text-xs text-slate-400">
              Whale activity often clusters before major events, news releases, or market-moving developments.
            </p>
          </div>
          <div className="rounded-lg bg-gunmetal/50 p-4">
            <div className="mb-2 text-sm font-semibold text-cyan-400">🔄 Copy Trading</div>
            <p className="text-xs text-slate-400">
              Track specific whale addresses with consistent performance to mirror high-performing traders.
            </p>
          </div>
        </div>
      </Panel>

      <Panel title="Raw API Data" description="Complete whale-flow API response">
        <JsonView value={whaleFlowQuery.data} />
      </Panel>
    </div>
  );
}
